import type { WallSnapshotDto } from '@pl/domain';
import { useEffect, useState } from 'react';
import { LiveWall } from './LiveWall';
import { Pairing } from './Pairing';
import { Preview } from './Preview';
import { BootScreen, useStageFit } from './stage';
import { fetchSnapshot } from './useWallData';

/** Waits between boot attempts while the server cannot be reached at all. */
const BOOT_RETRY_MS = 5000;

type Phase = { kind: 'boot' } | { kind: 'pairing' } | { kind: 'live'; snapshot: WallSnapshotDto; at: number } | { kind: 'preview' };

/**
 * The TV's whole app: pairing until an admin pairs it, then the live board for its site.
 *
 * Boot asks for the board with no site: the device cookie decides it on the server. A 401 means this
 * TV is not paired (or no longer is) and a 403 that its cookie is for a site it cannot have: both go
 * to pairing. No answer at all keeps asking, since the TV cannot tell a server that is down from a
 * network that is.
 *
 * A browser signed in as a person is not a TV. Its board needs a site, so the server answers 400
 * ("Choose a site"), or 403 `PASSWORD_CHANGE_REQUIRED` while the person is still on a temporary
 * password. Either goes to the preview, which lets them pick a site, and never asks again without one.
 */
export function WallApp({ reload = () => window.location.reload(), EventSourceImpl }: { reload?: () => void; EventSourceImpl?: typeof EventSource }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'boot' });
  const [attempt, setAttempt] = useState(0);
  const fit = useStageFit();

  useEffect(() => {
    if (phase.kind !== 'boot') return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      const r = await fetchSnapshot();
      if (cancelled) return;
      if (r.ok) setPhase({ kind: 'live', snapshot: r.snapshot, at: Date.now() });
      else if (r.status === 400 || (r.status === 403 && r.code === 'PASSWORD_CHANGE_REQUIRED')) setPhase({ kind: 'preview' });
      else if (r.status === 401 || r.status === 403) setPhase({ kind: 'pairing' });
      else retry = setTimeout(() => setAttempt((n) => n + 1), BOOT_RETRY_MS);
    })();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [phase.kind, attempt]);

  // Signed out while previewing: boot again, which finds a browser with no one in it, a TV to pair.
  if (phase.kind === 'preview') return <Preview reload={reload} EventSourceImpl={EventSourceImpl} onSignedOut={() => setPhase({ kind: 'boot' })} />;

  return (
    <div className="wall-app">
      <div className="w-scale" style={{ transform: `scale(${fit.scale})`, left: fit.left, top: fit.top }}>
        {phase.kind === 'boot' && (
          <BootScreen
            rail={
              <>
                <b>TV</b> · STARTING
              </>
            }
          >
            Connecting…
          </BootScreen>
        )}
        {phase.kind === 'pairing' && <Pairing onPaired={() => setPhase({ kind: 'boot' })} />}
        {phase.kind === 'live' && (
          <LiveWall key={phase.at} initial={phase.snapshot} onUnauthorized={() => setPhase({ kind: 'pairing' })} reload={reload} EventSourceImpl={EventSourceImpl} />
        )}
      </div>
    </div>
  );
}
